"use client"

import { FC, useState, useEffect, useMemo } from "react"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { 
  IconCalendar, 
  IconChevronRight, 
  IconChevronDown,
  IconCircle,
  IconCircleCheck,
  IconClock,
  IconFlag,
  IconUser
} from "@tabler/icons-react"
import { LinearChevronRightLine } from "marshmallow-icons"
import { format, addDays, startOfWeek, endOfWeek, eachDayOfInterval, differenceInDays } from "date-fns"
import { toast } from "sonner"
import { updateTask } from "@/db/space-tasks"
import { TaskPriority, TaskStatus } from "@/types/spaces"

interface Task {
  id: string
  space_id?: string
  title: string
  status: TaskStatus
  priority: TaskPriority
  start_date?: string
  due_date?: string
  assigned_to?: string
  assigned_to_name?: string
  assigned_to_avatar?: string
  time_estimate_minutes?: number
  progress?: number
  dependencies?: string[]
  folder_id?: string
  is_milestone?: boolean
  subtasks?: Task[]
}

interface GanttViewProps {
  tasks: Task[]
  onTaskUpdate?: (task: Task) => void
  onTaskClick?: (task: Task) => void
}

type ProcessedTask = Task & {
  level: number
  children: ProcessedTask[]
}

const getStatusColor = (status: string) => {
  switch (status) {
    case 'todo': return 'bg-gray-400'
    case 'in_progress': return 'bg-blue-500'
    case 'done': return 'bg-green-500'
    case 'blocked': return 'bg-red-500'
    case 'cancelled': return 'bg-gray-500'
    default: return 'bg-gray-400'
  }
}

const getPriorityColor = (priority: string) => {
  switch (priority) {
    case 'urgent': return 'text-red-600'
    case 'high': return 'text-orange-500'
    case 'medium': return 'text-yellow-500'
    case 'low': return 'text-green-500'
    default: return 'text-gray-500'
  }
}

export const GanttView: FC<GanttViewProps> = ({
  tasks,
  onTaskUpdate,
  onTaskClick
}) => {
  const [viewStart, setViewStart] = useState(() => startOfWeek(new Date()))
  const [viewEnd, setViewEnd] = useState(() => endOfWeek(addDays(new Date(), 30)))
  const [expandedTasks, setExpandedTasks] = useState<Set<string>>(new Set())
  type DragMode = "move" | "resize-start" | "resize-end"
  type DragPayload = { taskId: string; mode: DragMode }

  // Generate date columns for the timeline
  const dateColumns = useMemo(() => {
    return eachDayOfInterval({ start: viewStart, end: viewEnd })
  }, [viewStart, viewEnd])

  // Process tasks with hierarchical structure
  const processedTasks = useMemo(() => {
    const tasksMap = new Map<string, ProcessedTask>()
    const rootTasks: ProcessedTask[] = []

    // First pass: create all tasks with level 0
    tasks.forEach(task => {
      tasksMap.set(task.id, { ...task, level: 0, children: [] })
    })

    // Second pass: organize hierarchy (if parent/child relationships exist)
    tasks.forEach(task => {
      const processedTask = tasksMap.get(task.id)!
      
      if (task.folder_id && tasksMap.has(task.folder_id)) {
        // This is a subtask
        const parent = tasksMap.get(task.folder_id)!
        parent.children.push(processedTask)
        processedTask.level = parent.level + 1
      } else {
        // This is a root task
        rootTasks.push(processedTask)
      }
    })

    return rootTasks
  }, [tasks])

  // Flatten tasks for display with hierarchy
  const flattenedTasks = useMemo(() => {
    const flattened: (Task & { level: number; hasChildren: boolean })[] = []
    
    const addTask = (task: ProcessedTask) => {
      flattened.push({
        ...task,
        hasChildren: task.children.length > 0
      })
      
      if (expandedTasks.has(task.id)) {
        task.children.forEach(child => addTask(child))
      }
    }

    processedTasks.forEach(addTask)
    return flattened
  }, [processedTasks, expandedTasks])

  const toggleTaskExpansion = (taskId: string) => {
    const newExpanded = new Set(expandedTasks)
    if (newExpanded.has(taskId)) {
      newExpanded.delete(taskId)
    } else {
      newExpanded.add(taskId)
    }
    setExpandedTasks(newExpanded)
  }

  const getTaskPosition = (task: Task) => {
    if (!task.start_date || !task.due_date) return null

    const startDate = new Date(task.start_date)
    const endDate = new Date(task.due_date)
    
    const startDay = differenceInDays(startDate, viewStart)
    const duration = differenceInDays(endDate, startDate) + 1
    
    return {
      left: Math.max(0, startDay * 40), // 40px per day
      width: duration * 40,
      startDay,
      duration
    }
  }

  const handleDropOnDate = async (
    taskId: string,
    mode: DragMode,
    dayDate: Date
  ) => {
    const task = tasks.find(t => t.id === taskId)
    if (!task?.start_date || !task?.due_date) return

    const oldStart = new Date(task.start_date)
    const oldEnd = new Date(task.due_date)
    const originalDurationDays = differenceInDays(oldEnd, oldStart)
    const dayIndex = differenceInDays(dayDate, viewStart)
    const nextAnchor = addDays(viewStart, dayIndex)

    let nextStart = oldStart
    let nextEnd = oldEnd

    if (mode === "move") {
      nextStart = nextAnchor
      nextEnd = addDays(nextStart, originalDurationDays)
    } else if (mode === "resize-start") {
      nextStart = nextAnchor
    } else if (mode === "resize-end") {
      nextEnd = nextAnchor
    }

    if (nextEnd.getTime() < nextStart.getTime()) {
      nextEnd = nextStart
    }

    try {
      const updated = await updateTask(task.id, {
        space_id: task.space_id,
        start_date: nextStart.toISOString(),
        due_date: nextEnd.toISOString()
      })
      onTaskUpdate?.(updated as Task)
      toast.success("Gantt updated")
    } catch {
      toast.error("Failed to update gantt dates")
    }
  }

  return (
    <div className="gantt-view flex h-full min-w-0 flex-col overflow-hidden">
      {/* Header */}
      <div className="shrink-0 border-b p-3 sm:p-4">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <h2 className="text-lg font-semibold">Gantt Chart</h2>
          <div className="flex touch-pan-x items-center gap-2 overflow-x-auto lg:overflow-visible">
            <Button
              variant="outline"
              size="sm"
              className="min-h-10 shrink-0 touch-manipulation sm:min-h-9"
              onClick={() => {
                setViewStart(startOfWeek(addDays(viewStart, -7)))
                setViewEnd(endOfWeek(addDays(viewEnd, -7)))
              }}
            >
              Previous Week
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="min-h-10 shrink-0 touch-manipulation sm:min-h-9"
              onClick={() => {
                setViewStart(startOfWeek(new Date()))
                setViewEnd(endOfWeek(addDays(new Date(), 30)))
              }}
            >
              Today
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="min-h-10 shrink-0 touch-manipulation sm:min-h-9"
              onClick={() => {
                setViewStart(startOfWeek(addDays(viewStart, 7)))
                setViewEnd(endOfWeek(addDays(viewEnd, 7)))
              }}
            >
              Next Week
            </Button>
          </div>
        </div>
      </div>

      <div className="min-h-0 flex-1 touch-pan-x overflow-auto overscroll-contain">
      <div className="flex min-h-full min-w-[920px]">
        {/* Task List Panel */}
        <div className="bg-background flex w-[min(76vw,320px)] shrink-0 flex-col border-r sm:w-80">
          {/* Task List Header */}
          <div className="border-b p-4 font-medium">
            Tasks ({flattenedTasks.length})
          </div>
          
          {/* Task List */}
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
            {flattenedTasks.map((task) => (
              <div
                key={task.id}
                className="hover:bg-muted/50 flex min-h-12 cursor-pointer touch-manipulation items-center border-b p-2"
                style={{ paddingLeft: `${16 + task.level * 20}px` }}
                onClick={() => onTaskClick?.(task)}
              >
                {/* Expand/Collapse Button */}
                {task.hasChildren && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="mr-2 size-9 touch-manipulation p-0 sm:size-6"
                    onClick={(e) => {
                      e.stopPropagation()
                      toggleTaskExpansion(task.id)
                    }}
                  >
                    {expandedTasks.has(task.id) ? (
                      <IconChevronDown size={16} />
                    ) : (
                      <IconChevronRight size={16} />
                    )}
                  </Button>
                )}
                
                {/* Task Status Icon */}
                <div className="mr-2">
                  {task.is_milestone ? (
                    <IconFlag size={16} className="text-yellow-500" />
                  ) : task.status === 'done' ? (
                    <IconCircleCheck size={16} className="text-green-500" />
                  ) : (
                    <IconCircle size={16} className={getStatusColor(task.status).replace('bg-', 'text-')} />
                  )}
                </div>
                
                {/* Task Info */}
                <div className="min-w-0 flex-1">
                  <div className="break-words text-sm font-medium">{task.title}</div>
                  <div className="text-muted-foreground flex items-center gap-2 text-xs">
                    {task.assigned_to && (
                      <div className="relative inline-flex size-5 items-center justify-center overflow-hidden rounded-full border">
                        {/* eslint-disable-next-line @next/next/no-img-element */}
                        <img
                          src={task.assigned_to_avatar || ''}
                          alt="assignee"
                          className="size-full object-cover"
                          onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none' }}
                        />
                        <span className="bg-muted absolute inset-0 flex items-center justify-center text-[9px] font-medium">
                          {(task.assigned_to_name || 'U')
                            .toString()
                            .charAt(0)
                            .toUpperCase()}
                        </span>
                      </div>
                    )}
                    {task.time_estimate_minutes && (
                      <span className="flex items-center gap-1">
                        <IconClock size={12} />
                        {Math.round(task.time_estimate_minutes / 60)}h
                      </span>
                    )}
                    <IconFlag size={12} className={getPriorityColor(task.priority || 'medium')} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Timeline Panel */}
        <div className="min-w-0 flex-1 overflow-x-auto">
          {/* Date Headers */}
          <div className="bg-muted/30 flex border-b">
            {dateColumns.map((date) => (
              <div
                key={date.toISOString()}
                className="w-10 min-w-10 border-r p-2 text-center text-xs"
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  const raw = e.dataTransfer.getData("text/plain")
                  if (!raw) return
                  try {
                    const parsed = JSON.parse(raw) as DragPayload
                    void handleDropOnDate(parsed.taskId, parsed.mode, date)
                  } catch {
                    return
                  }
                }}
              >
                <div className="font-medium">
                  {format(date, 'EEE')}
                </div>
                <div className="text-muted-foreground">
                  {format(date, 'd')}
                </div>
              </div>
            ))}
          </div>

          {/* Task Timeline Bars */}
          <div className="relative">
            {flattenedTasks.map((task, index) => {
              const position = getTaskPosition(task)
              
              return (
                <div
                  key={task.id}
                  className="relative flex h-12 items-center border-b"
                  style={{ paddingLeft: `${task.level * 20}px` }}
                >
                  {/* Background grid */}
                  <div className="absolute inset-0 flex">
                    {dateColumns.map((date) => (
                      <div
                        key={date.toISOString()}
                        className="border-muted/30 w-10 min-w-10 border-r"
                      />
                    ))}
                  </div>

                  {/* Task Bar */}
                  {position && position.startDay >= 0 && (
                    <div
                      className={`absolute min-h-8 touch-manipulation rounded sm:min-h-6 ${getStatusColor(task.status)} cursor-move opacity-90 transition-opacity hover:opacity-100`}
                      style={{
                        left: `${position.left}px`,
                        width: `${Math.max(position.width, 20)}px`
                      }}
                      draggable
                      onDragStart={(e) => {
                        const payload: DragPayload = { taskId: task.id, mode: "move" }
                        e.dataTransfer.setData("text/plain", JSON.stringify(payload))
                        e.dataTransfer.effectAllowed = "move"
                      }}
                      onClick={() => onTaskClick?.(task)}
                      title={`${task.title} (${format(new Date(task.start_date!), 'MMM d')} - ${format(new Date(task.due_date!), 'MMM d')})`}
                    >
                      <div
                        className="absolute left-0 top-0 h-full w-4 cursor-ew-resize sm:w-2"
                        draggable
                        onClick={(evt) => evt.stopPropagation()}
                        onDragStart={(e) => {
                          const payload: DragPayload = { taskId: task.id, mode: "resize-start" }
                          e.dataTransfer.setData("text/plain", JSON.stringify(payload))
                          e.dataTransfer.effectAllowed = "move"
                        }}
                      />
                      <div
                        className="absolute right-0 top-0 h-full w-4 cursor-ew-resize sm:w-2"
                        draggable
                        onClick={(evt) => evt.stopPropagation()}
                        onDragStart={(e) => {
                          const payload: DragPayload = { taskId: task.id, mode: "resize-end" }
                          e.dataTransfer.setData("text/plain", JSON.stringify(payload))
                          e.dataTransfer.effectAllowed = "move"
                        }}
                      />
                      <div className="truncate px-2 py-1 text-xs font-medium text-white">
                        {task.is_milestone ? '◆' : task.title}
                      </div>
                      
                      {/* Progress Bar */}
                      {task.progress && task.progress > 0 && (
                        <div
                          className="absolute bottom-0 left-0 h-1 rounded-b bg-white/50"
                          style={{ width: `${task.progress}%` }}
                        />
                      )}
                    </div>
                  )}

                  {/* Dependencies */}
                  {task.dependencies?.map((depId) => {
                    const depTask = tasks.find(t => t.id === depId)
                    const depPosition = depTask ? getTaskPosition(depTask) : null
                    
                    if (depPosition && position) {
                      return (
                        <div
                          key={`dep-${task.id}-${depId}`}
                          className="absolute border-t-2 border-blue-400"
                          style={{
                            left: `${depPosition.left + depPosition.width}px`,
                            width: `${position.left - (depPosition.left + depPosition.width)}px`,
                            top: '24px'
                          }}
                        >
                          <LinearChevronRightLine width={12} height={12} className="absolute right-0 top-[-6px] text-blue-400" />
                        </div>
                      )
                    }
                    return null
                  })}
                </div>
              )
            })}
          </div>
        </div>
      </div>
      </div>

      {/* Legend */}
      <div className="bg-muted/30 border-t p-4">
        <div className="flex items-center gap-6 text-xs">
          <div className="flex items-center gap-2">
            <IconFlag size={14} className="text-yellow-500" />
            <span>Milestone</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="h-2 w-4 rounded bg-blue-500" />
            <span>In Progress</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="h-2 w-4 rounded bg-green-500" />
            <span>Completed</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="h-2 w-4 rounded bg-red-500" />
            <span>Blocked</span>
          </div>
          <div className="flex items-center gap-2">
            <LinearChevronRightLine width={14} height={14} className="text-blue-400" />
            <span>Dependency</span>
          </div>
        </div>
      </div>
    </div>
  )
}
